import React from 'react';
import { Subject, of, merge, Observable, race, BehaviorSubject, zip } from 'rxjs';
import { map, mergeMap, catchError, take, takeUntil, switchMap, takeWhile } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { FormattedMessage } from 'react-intl';
import { NotificationService } from 'ui-components/services/notification-service';

import * as EntitiesActions from '../../../store/entities/entities.actions';
import Global from '../../../store/global-selectors';
import { launchLabelBatchStatusModalForJob } from './batches.purchase.helpers';
import { BulkPurchaseNotificationService } from '../../../services/bulk-purchase-noto-service';
import * as Api from '../grid/batches.grid.api';
import * as BatchApi from '../../../store/batches/batches.api';
import * as Actions from './batches.purchase.actions';
import { normalizeDataArray, buildEntityPayload, buildJobData } from './data-helpers';
import {
  getQuickshipPropsFromState,
  shouldLaunchLbsModal,
  determineShouldShowErrorsOrWarnings,
  hasShowErrorsEnabled,
  hasShowWarningsEnabled,
} from '../../quickship/quickship.helpers';
import {
  BATCH_JOB_ENTITY_GROUP_NAME,
  BATCH_JOB_ENTITIY_ID_ATTRIBUTE,
  BATCH_ENTITY_GROUP_NAME,
  BATCH_ENTITY_ID_ATTRIBUTE,
} from './constants';
import { VALIDATION_TIMEOUT_ERROR } from '../../bulk/bulk-error-types';
import { requestAndGetValidationMessages } from '../../label-purchase-validation';
import { messages } from './messages';
import { BulkErrorsAndWarningsService } from '../../../services/bulk-errors-and-warnings-service';
import { emptyArray } from 'ui-components/default-props/empty';
import { LabelBatchStatusStep } from '../../../enums/label-batch-status-step';
import { prepareReduxUpdatesOnJobDone } from './validation-message-processing-helpers';

const errorsAndWarningsTypes = [
  BulkErrorsAndWarningsService.getBatchValidatedErrorType(),
  BulkErrorsAndWarningsService.getBatchValidatedWarningType(),
];

export const showWeAreProcessingNotification = (dontHide = false) => {
  const props = {
    title: <FormattedMessage {...messages.processingBatchTitle} />,
    content: <FormattedMessage {...messages.processingBatchContent} />,
    notificationType: 'info',
    dontHide,
  };
  return NotificationService.showNotification({ ...props });
};

export const handleValidationApiResponse = ({ action$, getState, fulfillmentPlanSetId, quickshipProps }) => {
  return race(
    action$.pipe(
      ofType(Actions.VALIDATE_BATCHES_SUCCESS),
      // by taking 1 we only launch the modal one time. The modal observer activates the responses to seeing the pusher validation
      // messages on the stream for the first and any subsequent revalidations
      take(1),
      mergeMap((successAction) => {
        const { jobGuids } = successAction;

        return new Observable((observer) => {
          if (Global.selectBatchesPurchaseLabelBatchStatusModalId(getState())) {
            // this modalId would be from a different observable. We need to clear that to make sure any future modalIds are from this observable.
            observer.next(of(Actions.setBatchesLbsModalId('')));
          }

          // check to see if we need the modal
          if (shouldLaunchLbsModal(quickshipProps)) {
            launchLabelBatchStatusModalForJob(jobGuids, fulfillmentPlanSetId, getState, observer);
          }
        }).pipe(
          mergeMap((next$) => next$),
          takeUntil(action$.pipe(ofType(Actions.BATCH_STATUS_MODAL_CLOSED))),
        );
      }),
    ),
    action$.pipe(ofType(Actions.VALIDATE_BATCHES_FAILURE), take(1)),
  );
};

const onTimeout = (batchEntityToUpdate) => {
  const copy = { ...batchEntityToUpdate };
  copy.error = VALIDATION_TIMEOUT_ERROR;
  const errorEntities = buildEntityPayload(normalizeDataArray(BATCH_ENTITY_GROUP_NAME, BATCH_ENTITY_ID_ATTRIBUTE, [copy]));
  return EntitiesActions.entitiesLoad(errorEntities);
};

const processNonFinalMessages = (pusherMessages) => {
  return pusherMessages.reduce(
    (acc, { data }) => {
      return {
        total: data?.total,
        completedCount: (acc.completedCount || 0) + (data?.count || 0),
        messages: [...acc.messages, ...(data?.messages || emptyArray)],
      };
    },
    { total: 0, completedCount: 0, messages: [] },
  );
};

const processFinalMessage = (finalMessage, processedMessages) => {
  const jobId = finalMessage?.messageGuid;

  const errorOrWarningMessages = processedMessages.messages
    .filter((m) => errorsAndWarningsTypes.includes(m?.Type))
    .map((messageData) => ({
      ...messageData,
      id: messageData.OrderID.toString(),
      fulfillmentPlanId: messageData.OrderID.toString(),
      message: messageData.Message,
      type: messageData.Type,
    }))
    .sort((a, b) => a.fulfillmentPlanId - b.fulfillmentPlanId);

  const filteredErrors = errorOrWarningMessages.filter((err) => err?.type === BulkErrorsAndWarningsService.getBatchValidatedErrorType());
  const filteredWarnings = errorOrWarningMessages.filter(
    (err) => err?.type === BulkErrorsAndWarningsService.getBatchValidatedWarningType(),
  );

  const errorsAndWarnings = [];

  if (filteredErrors.length) {
    errorsAndWarnings.push(BulkErrorsAndWarningsService.getLabelBatchCreateErrors(filteredErrors));
  }
  if (filteredWarnings.length) {
    errorsAndWarnings.push(BulkErrorsAndWarningsService.getLabelBatchCreateWarnings(filteredWarnings));
  }

  return { jobId, errorsAndWarnings };
};

const checkLabelsAlreadyPurchased = (fulfillmentPlanSetId) => {
  return BatchApi.canPurchaseLabelsForBatch([fulfillmentPlanSetId]).pipe(
    map((data) => {
      const allCanPurchase = data.every((x) => x.canPurchaseLabel);
      const activeLabelWarnings = data
        .filter((x) => !x.canPurchaseLabel)
        .map((bad) => ({
          ...bad,
          id: bad.fulfillmentPlanId,
          fulfillmentPlanId: bad.fulfillmentPlanId,
        }));
      return {
        allCanPurchase,
        activeLabelWarnings,
      };
    }),
  );
};

const decideNextAction = (pusherErrorsAndWarnings, allCanPurchase, activeLabelWarnings, getState, fulfillmentPlanSetId) => {
  const quickshipProps = getQuickshipPropsFromState(getState());
  if ((allCanPurchase && !pusherErrorsAndWarnings.length) || !determineShouldShowErrorsOrWarnings(quickshipProps)) {
    return Actions.costReviewBatches(fulfillmentPlanSetId);
  }
  const { data: warnings } = pusherErrorsAndWarnings.find(({ type }) => type === 'warning') || { data: [] };
  const { data: errors } = pusherErrorsAndWarnings.find(({ type }) => type === 'error') || { data: [] };

  // quickship skip to cost review
  if (
    quickshipProps.shouldUseQuickship &&
    // if we have warnings but don't want to see them or have errors but don't want to see them, skip
    ((warnings.length && !hasShowWarningsEnabled(quickshipProps.userQuickshipSettings)) ||
      (errors.length && !hasShowErrorsEnabled(quickshipProps.userQuickshipSettings)))
  ) {
    return Actions.costReviewBatches(fulfillmentPlanSetId);
  }

  // BTC: this fn converts active labels to warning status. Should be error?
  const activeLabelWarningsArray = BulkErrorsAndWarningsService.mapErrorsAndWarningsToExpectedModel(
    BulkErrorsAndWarningsService.getLabelActiveWarnings(activeLabelWarnings).data,
  );
  const errsAndWarnings = BulkErrorsAndWarningsService.mapErrorsAndWarningsToExpectedModel([
    ...errors,
    ...warnings,
    ...activeLabelWarningsArray,
  ]);

  const batchEntity = Global.selectEntitiesBatchByFulfillmentPlanSetId(getState(), { fulfillmentPlanSetId });
  const updatedEntity = {
    ...batchEntity,
    errorsAndWarnings: errsAndWarnings,
    step: LabelBatchStatusStep.ErrorOrWarning.ordinal,
  };
  const normalizedBatch = normalizeDataArray(BATCH_ENTITY_GROUP_NAME, BATCH_ENTITY_ID_ATTRIBUTE, [updatedEntity]);
  const batchPayload = buildEntityPayload(normalizedBatch);
  return EntitiesActions.entitiesLoad(batchPayload);
};

export const startBatchValidation = ({ action$, getState, fulfillmentPlanSetId, pusherTimeoutMs = 10000 }) => {
  const startValidationSubject = new BehaviorSubject({ isRetry: false });
  const onTimeoutSubject = new Subject();
  return startValidationSubject
    .pipe(
      mergeMap(({ isRetry }) => {
        return requestAndGetValidationMessages({
          curriedApi: () => Api.validateBatches(fulfillmentPlanSetId),
          action$,
          isRetry,
          onValidationTimeout: (jobGuids) => {
            const batchEntityToUpdate = {
              ...Global.selectEntitiesBatchByFulfillmentPlanSetId(getState(), {
                fulfillmentPlanSetId,
              }),
              validateJobIds: jobGuids,
            };
            onTimeoutSubject.next(onTimeout(batchEntityToUpdate));
          },
          onRetryJob: () => startValidationSubject.next({ isRetry: true }),
          pusherTimeoutMs,
          logContext: {
            fulfillmentPlanSetId,
            purchasePoint: 'batch',
          },
        }).pipe(
          // BTC: note that the below array is empty if all FPs in plan set are already purchased
          switchMap(({ jobGuids, findMessage$ }) => {
            const batchEntityToUpdate = {
              ...Global.selectEntitiesBatchByFulfillmentPlanSetId(getState(), {
                fulfillmentPlanSetId,
              }),
              validateJobIds: jobGuids, // will need to concat when retries are setup
            };

            const { jobs, jobIdsToFpSetIdMap } = buildJobData(jobGuids, fulfillmentPlanSetId);
            const normalizedJobs = normalizeDataArray(BATCH_JOB_ENTITY_GROUP_NAME, BATCH_JOB_ENTITIY_ID_ATTRIBUTE, jobs);
            const normalizedBatches = normalizeDataArray(BATCH_ENTITY_GROUP_NAME, BATCH_ENTITY_ID_ATTRIBUTE, [batchEntityToUpdate]);

            const entities = buildEntityPayload({
              ...normalizedJobs,
              ...normalizedBatches,
            });

            const next$ = of(
              // hits reducer
              Actions.validateBatchesSuccess(jobIdsToFpSetIdMap, jobGuids),
              EntitiesActions.entitiesLoad(entities),
            );

            if (!jobGuids.length) {
              return merge(
                next$,
                checkLabelsAlreadyPurchased(fulfillmentPlanSetId).pipe(
                  switchMap(({ allCanPurchase, activeLabelWarnings }) => {
                    return of(decideNextAction([], allCanPurchase, activeLabelWarnings, getState, fulfillmentPlanSetId));
                  }),
                ),
              );
            }

            return merge(
              next$,
              onTimeoutSubject,
              zip(
                checkLabelsAlreadyPurchased(fulfillmentPlanSetId),
                findMessage$.pipe(
                  take(1),
                  map((normalizedMessages) => {
                    const nonFinalMessages = normalizedMessages.filter((message) => !message?.data?.final);
                    const finalMessage = normalizedMessages.find((message) => message?.data?.final);
                    const processedMessages = processNonFinalMessages(nonFinalMessages);
                    return processFinalMessage(finalMessage, processedMessages);
                  }),
                ),
              ).pipe(
                switchMap(([{ allCanPurchase, activeLabelWarnings }, { jobId, errorsAndWarnings }]) => {
                  const state = getState();
                  const updateJob = prepareReduxUpdatesOnJobDone(Global.selectEntitiesBatchJobByJobId(state, { jobId }));

                  const nextAction = decideNextAction(
                    errorsAndWarnings,
                    allCanPurchase,
                    activeLabelWarnings,
                    getState,
                    fulfillmentPlanSetId,
                  );

                  return of(updateJob, nextAction, { shouldComplete: true });
                }),
              ),
            );
          }),
        );
      }),
      // take until we get the final message or close modal
      takeWhile((next) => !next?.shouldComplete),
      takeUntil(action$.pipe(ofType(Actions.BATCH_STATUS_MODAL_CLOSED))),
    )
    .pipe(
      catchError(() => {
        BulkPurchaseNotificationService.showGenericHttpNotification();
        return of(Actions.validateBatchesFailure());
      }),
    );
};

export const waitForEndOfValidation = (action$) => {
  return action$.pipe(ofType(Actions.COST_REVIEW_BATCHES_REQUEST));
};
